/**
 * La campana de la cabecera, con lo pendiente contado · 13-10-2026
 *
 * Lo pidió el primer club: «que salga algo llamativo y te diga que tienes X
 * alertas». El número va en rojo si alguna es urgente y en ámbar si no; sin nada,
 * la campana se queda en gris y sin número, que también es información.
 *
 * Al pulsarla se abre la hoja (`HojaDePendientes`): en el ordenador, bajo la
 * campana; en el móvil, desde abajo, donde llega el pulgar.
 */
import { useCallback, useRef, useState } from 'react';
import { Icono } from '@/shared/ui/Icono';
import { useCerrarAlSalir } from '@/shared/ui/useCerrarAlSalir';
import { ListaDePendientes } from '@/shared/ui/pendientes/ListaDePendientes';
import { urgentes, type Pendiente } from '@/shared/ui/pendientes/tipos';

export function Campana({
  pendientes,
  cargando,
  alIr,
  titulo = 'Pendiente',
}: {
  pendientes: readonly Pendiente[] | undefined;
  cargando: boolean;
  alIr: (destino: string) => void;
  titulo?: string;
}) {
  const [abierta, setAbierta] = useState(false);
  const caja = useRef<HTMLDivElement>(null);
  const total = pendientes?.length ?? 0;
  const hayUrgentes = urgentes(pendientes ?? []).length > 0;

  const cerrar = useCallback(() => setAbierta(false), []);
  useCerrarAlSalir(abierta, caja, cerrar);

  const etiqueta =
    total === 0
      ? 'Nada pendiente'
      : `${total} ${total === 1 ? 'cosa pendiente' : 'cosas pendientes'}${hayUrgentes ? ', alguna urgente' : ''}`;

  return (
    <div className="campana-caja" ref={caja}>
      <button
        type="button"
        className={`btn-plano btn-icono campana${total > 0 ? ' campana-con-algo' : ''}`}
        aria-label={etiqueta}
        title={etiqueta}
        aria-expanded={abierta}
        aria-haspopup="dialog"
        onClick={() => setAbierta((x) => !x)}
      >
        <Icono nombre="campana" tamano={20} />
        {total > 0 && (
          <span
            className={`campana-numero${hayUrgentes ? ' campana-urgente' : ''}`}
            aria-hidden="true"
          >
            {total > 9 ? '9+' : total}
          </span>
        )}
      </button>

      {abierta && (
        <>
          {/* El fondo solo se ve en el móvil: ahí la hoja tapa la pantalla. */}
          <div
            className="hoja-fondo"
            aria-hidden="true"
            onClick={() => setAbierta(false)}
          />
          <div className="hoja" role="dialog" aria-label={titulo}>
            <div className="hoja-cabeza">
              <strong>{titulo}</strong>
              <button
                type="button"
                className="btn-plano btn-icono"
                aria-label="Cerrar"
                onClick={() => setAbierta(false)}
              >
                <Icono nombre="cerrar" />
              </button>
            </div>
            {cargando && !pendientes ? (
              <p className="tenue">Mirando lo pendiente…</p>
            ) : (
              <ListaDePendientes
                pendientes={pendientes ?? []}
                alIr={(destino) => {
                  setAbierta(false);
                  alIr(destino);
                }}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}
